// Notification policy — what the org sends, to whom, and when.
//
// TWO LAYERS, and the owner does not win both:
//
//   1. ASSIGNMENT — orgs/{org}/config/notificaciones, written by owners.
//      Answers "who should get what". This module models it.
//   2. CONSENT — usuarios/{uid}.emailPreferences + .emailUnsubscribedAll,
//      written by each user in Configuración → Correo. Already existed.
//
// Effective send = the org says yes AND the person has not opted out. Never the
// other way around. The Notificaciones screen must SHOW when a member opted out
// of something the owner assigned — otherwise it is a silent failure and turns
// into "le puse el resumen y no le llega".
//
// The canonical list registry is Python: backend-nyx/src/config/email_lists.py.
// `listKey` below points into it, and functions/index.js carries a small Node
// twin of effective_send_allowed. Three places, one contract — keep in lockstep.
//
// Pure functions only. No React, no Firestore.

// Sections a digest can be composed of. Phase 1 ships `pendientes` alone, but
// the registry exists from the start because "lo que me falta" is eventually
// also vencimientos, cobranza, emisiones and comisiones — all of which Pulso
// and the reportes already compute. Adding one later is an entry here, not a
// rewrite of the email.
export const SECCIONES = {
  PENDIENTES: 'pendientes',
};

export const SECCION_META = {
  pendientes: {
    label: 'Pendientes',
    descripcion: 'Lo que falta entregar, vencidos primero.',
    // Who may receive it at all. A section restricted to owners never appears
    // in an operator's column, however the grid is clicked.
    roles: ['owner', 'operator'],
    disponible: true,
  },
};

// What the org can send. Each entry is one column of the owner's grid.
//
// `listKey` is the consent list it rides on (EMAIL_LISTS in email_lists.py).
// `alcance` is whose data it reports: 'mios' = the member's own assigned
// clientes/contratos, 'org' = the whole cartera.
export const CANALES = {
  // The canal id stays `resumen_semanal` for storage compatibility (grid
  // cells, resumenes stamps) although its cadence is now per person.
  RESUMEN_SEMANAL: 'resumen_semanal',
  RESUMEN_NEGOCIO: 'resumen_negocio',
  PENDIENTE_ASIGNADO: 'pendiente_asignado',
};

// How often a person gets "Lo que te falta". Chosen by the PERSON in
// Configuración → Correo (usuarios.emailPreferences.frecuenciaResumen); the
// owner only fixes the org default for whoever has not chosen. Weekends are
// out of `diario` unless the org turns `finDeSemana` on (Fran, 2026-11-04:
// the daily was landing on Saturdays and Sundays and "los diarios son mucho").
export const FRECUENCIAS = {
  diario: { label: 'Diario, de lunes a viernes', dias: [1, 2, 3, 4, 5], enviarVacio: false },
  lun_jue: { label: 'Lunes y jueves', dias: [1, 4], enviarVacio: false },
  semanal: { label: 'Una vez a la semana', dias: null, enviarVacio: true },
  nunca: { label: 'No recibir', dias: [], enviarVacio: false },
};
export const FRECUENCIA_ORDEN = ['diario', 'lun_jue', 'semanal', 'nunca'];
export const FRECUENCIA_DEFAULT = 'semanal';
export const esFrecuencia = (v) => typeof v === 'string' && Object.keys(FRECUENCIAS).includes(v);

// Three mails, not five (Fran, 2026-09-28: "demasiados correos que dicen
// básicamente lo mismo"):
//   - "Lo que te falta": pendientes + fianzas por vencer + facturas sin pagar
//     + requeridas sin iniciar. The org says WHO gets it; each person picks
//     HOW OFTEN (FRECUENCIAS), weekly unless they choose otherwise. An
//     operator gets what is assigned to them, an owner the whole cartera —
//     alcance follows the rol, so the old "toda la cartera" canal is gone.
//   - "Lo que pasó": the owner's business summary of the week (emisión,
//     prima, cobranza, comisiones, metas). `sinMontos` on the org config
//     strips the amounts.
//   - "Te asignaron": the one event-driven mail. "Pendiente vencido" was
//     dropped — the same row appears in the next digest.
export const CANAL_META = {
  resumen_semanal: {
    label: 'Lo que te falta',
    detalle: 'Cada quien elige',
    descripcion: 'Pendientes, fianzas por vencer, facturas sin pagar y requeridas sin iniciar.',
    listKey: 'resumen_agente',
    programado: true,
    // 'persona': the weekday comes from the member's FRECUENCIA, not the canal.
    frecuencia: 'persona',
    alcance: 'rol',
    roles: ['owner', 'operator'],
    porDefecto: true,
    // Only the weekly cadence sends an empty digest (see enviarVacioPara).
    enviarVacio: true,
  },
  resumen_negocio: {
    label: 'Lo que pasó',
    detalle: 'Semanal, administradores',
    descripcion: 'Resumen del negocio: emisión, prima, cobranza, comisiones y metas.',
    listKey: 'resumen_negocio',
    programado: true,
    frecuencia: 'semanal',
    alcance: 'org',
    roles: ['owner'],
    porDefecto: true,
    enviarVacio: true,
  },
  pendiente_asignado: {
    label: 'Te asignaron',
    detalle: 'Al momento',
    descripcion: 'Aviso cuando un compañero le asigna un pendiente.',
    listKey: 'pendiente_asignado',
    programado: false,
    frecuencia: null,
    alcance: 'mios',
    roles: ['owner', 'operator'],
    porDefecto: true,
    enviarVacio: false,
  },
};

export const CANAL_ORDEN = [
  CANALES.RESUMEN_SEMANAL,
  CANALES.RESUMEN_NEGOCIO,
  CANALES.PENDIENTE_ASIGNADO,
];

// Only broker roles ever receive these. Client-portal users are out of the
// scheduler entirely.
export const ROLES_ELEGIBLES = ['owner', 'operator'];

// The four Mexican time zones, as IANA ids. Deliberately not the full IANA
// list: this is a Mexican product and four options fit in a select.
//
// Mexico dropped DST in 2022 — EXCEPT the northern border municipalities, so
// America/Tijuana still shifts with the US. That is why the zone is a stored
// field and not a constant: an org set to 8:00 and sent in centro time would
// land at 6:00 in Tijuana in winter and 7:00 in summer, drifting with no
// visible explanation.
export const ZONAS = [
  { value: 'America/Mexico_City', label: 'Centro (CDMX, Monterrey, Mérida)' },
  { value: 'America/Mazatlan', label: 'Pacífico (BCS, Sinaloa, Sonora)' },
  { value: 'America/Tijuana', label: 'Noroeste (Tijuana, Mexicali)' },
  { value: 'America/Cancun', label: 'Sureste (Quintana Roo)' },
];

export const ZONA_DEFAULT = 'America/Mexico_City';
export const HORA_DEFAULT = 8;
// ISO weekday, 1 = Monday. The weekly digest is a Monday-morning ritual.
export const DIA_SEMANAL_DEFAULT = 1;

/**
 * The org's zone, detected from the browser rather than asked.
 *
 * Falls back to centro when the machine reports anything outside Mexico — an
 * owner on a trip or behind a VPN should not silently reconfigure the org.
 *
 * @param {string} [detectada]  - defaults to Intl's resolved zone
 * @return {string} one of ZONAS
 */
export function zonaDetectada(detectada = null) {
  let zona = detectada;
  if (!zona) {
    try {
      zona = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      zona = null;
    }
  }
  return ZONAS.some((z) => z.value === zona) ? zona : ZONA_DEFAULT;
}

export const CONFIG_DEFAULTS = {
  zona: ZONA_DEFAULT,
  hora: HORA_DEFAULT,
  diaSemanal: DIA_SEMANAL_DEFAULT,
  // { [uid]: { [canal]: boolean } }. A uid absent from here, or a canal absent
  // from a uid's entry, falls through to CANAL_META.porDefecto — so a new team
  // member starts correctly configured without anyone touching this screen.
  miembros: {},
  // When true, that fallback is "off" instead of the product default: a member
  // with no explicit cell gets nothing until the owner ticks them. Built for
  // orgs that want to opt people in one by one (and for demo orgs, where a
  // seeded member must never receive mail by accident).
  nuevosApagados: false,
  // "Lo que pasó" without figures: the mail names what moved (fianzas,
  // facturas, comisiones) but no pesos. For orgs whose inboxes get forwarded.
  sinMontos: false,
  // `diario` also on Saturday and Sunday. Off: a despacho rests on weekends
  // and Monday's mail carries what piled up.
  finDeSemana: false,
  // Cadence of "Lo que te falta" for members who have not chosen their own.
  frecuenciaDefault: FRECUENCIA_DEFAULT,
};

/**
 * Fill an org's stored config with defaults. An absent doc resolves to the
 * defaults, so the feature works before anyone opens the screen.
 *
 * @param {Object|null} stored  - orgs/{org}/config/notificaciones
 * @return {typeof CONFIG_DEFAULTS}
 */
export function resolverConfig(stored) {
  const s = stored || {};
  const hora = Number(s.hora);
  return {
    zona: ZONAS.some((z) => z.value === s.zona) ? s.zona : ZONA_DEFAULT,
    hora: Number.isInteger(hora) && hora >= 0 && hora <= 23 ? hora : HORA_DEFAULT,
    diaSemanal: Number.isInteger(Number(s.diaSemanal)) &&
      Number(s.diaSemanal) >= 1 && Number(s.diaSemanal) <= 7
      ? Number(s.diaSemanal) : DIA_SEMANAL_DEFAULT,
    miembros: (s.miembros && typeof s.miembros === 'object') ? s.miembros : {},
    nuevosApagados: s.nuevosApagados === true,
    sinMontos: s.sinMontos === true,
    finDeSemana: s.finDeSemana === true,
    frecuenciaDefault: esFrecuencia(s.frecuenciaDefault) && s.frecuenciaDefault !== 'nunca'
      ? s.frecuenciaDefault : FRECUENCIA_DEFAULT,
  };
}

// ── Cadence of "Lo que te falta" ────────────────────────────────────────────

/**
 * How often THIS person gets the digest.
 *
 * Precedence: what the person chose in Correo; else the legacy per-member
 * `resumen_diario: true` cell an owner may have ticked before cadence became
 * personal (kept as 'diario' so nobody silently drops to weekly); else the
 * org default.
 *
 * @param {Object} config  - resolved config
 * @param {Object} usuario  - usuarios/{uid} doc with id
 * @return {string} one of FRECUENCIA_ORDEN
 */
export function frecuenciaDe(config, usuario) {
  const u = usuario || {};
  const elegida = (u.emailPreferences || {}).frecuenciaResumen;
  if (esFrecuencia(elegida)) return elegida;
  const delMiembro = config.miembros && config.miembros[u.id];
  if (delMiembro && delMiembro.resumen_diario === true) return 'diario';
  return config.frecuenciaDefault || FRECUENCIA_DEFAULT;
}

/** True when the person chose their own cadence (vs. the org default). */
export function frecuenciaEsPropia(usuario) {
  return esFrecuencia(((usuario || {}).emailPreferences || {}).frecuenciaResumen);
}

/**
 * Does the digest go out on this weekday for this cadence?
 * @param {string} frecuencia
 * @param {number} dia  - ISO weekday from relojLocal (1 = Monday)
 * @param {Object} config  - resolved config (diaSemanal, finDeSemana)
 * @return {boolean}
 */
export function diaToca(frecuencia, dia, config) {
  const meta = FRECUENCIAS[frecuencia];
  if (!meta) return false;
  if (frecuencia === 'semanal') return dia === config.diaSemanal;
  if (frecuencia === 'diario' && config.finDeSemana) return dia >= 1 && dia <= 7;
  return meta.dias.includes(dia);
}

/** An empty digest only goes out on the weekly cadence (the clean-week ritual). */
export function enviarVacioPara(frecuencia) {
  return Boolean(FRECUENCIAS[frecuencia] && FRECUENCIAS[frecuencia].enviarVacio);
}

/**
 * Whether a canal makes sense for a role at all. A canal the role cannot
 * receive is not rendered, so the grid never offers an impossible cell.
 *
 * @param {string} canal
 * @param {string} rol
 * @return {boolean}
 */
export function canalAplicaARol(canal, rol) {
  const meta = CANAL_META[canal];
  return Boolean(meta && meta.roles.includes(rol));
}

/**
 * What the ORG says for one member and canal — layer 1 only.
 *
 * Precedence: the member's explicit value, else the canal default. A role that
 * cannot receive the canal is always false, whatever is stored (so demoting an
 * owner to operator silently stops the org-wide digest instead of leaking it).
 *
 * @param {Object} config  - resolved config
 * @param {string} uid
 * @param {string} rol
 * @param {string} canal
 * @return {boolean}
 */
export function orgAsigna(config, uid, rol, canal) {
  if (!canalAplicaARol(canal, rol)) return false;
  const delMiembro = config.miembros && config.miembros[uid];
  // Legacy: before cadence was personal an owner could tick "diario" and
  // untick "semanal" for a member. That person was receiving mail; keep it
  // flowing (as 'diario', see frecuenciaDe) instead of silently dropping them.
  if (canal === CANALES.RESUMEN_SEMANAL && delMiembro && delMiembro.resumen_diario === true) {
    return true;
  }
  if (delMiembro && typeof delMiembro[canal] === 'boolean') {
    return delMiembro[canal];
  }
  if (config.nuevosApagados) return false;
  return Boolean(CANAL_META[canal].porDefecto);
}

/**
 * Layer 2: has this person opted out? Mirrors effective_send_allowed in
 * backend-nyx/src/config/email_lists.py — a missing preference means opted IN.
 *
 * @param {Object} usuario  - the usuarios/{uid} doc
 * @param {string} canal
 * @return {boolean} true when the person still accepts it
 */
export function personaAcepta(usuario, canal) {
  const meta = CANAL_META[canal];
  if (!meta) return false;
  const u = usuario || {};
  if (u.emailUnsubscribedAll === true) return false;
  const prefs = u.emailPreferences || {};
  const valor = prefs[meta.listKey];
  return valor === undefined || valor === null ? true : Boolean(valor);
}

/**
 * Both layers. This is what the scheduler asks before sending.
 *
 * @param {Object} config  - resolved org config
 * @param {Object} usuario  - the usuarios/{uid} doc (must carry id + rol)
 * @param {string} canal
 * @return {boolean}
 */
export function seEnvia(config, usuario, canal) {
  if (!usuario || !ROLES_ELEGIBLES.includes(usuario.rol)) return false;
  return orgAsigna(config, usuario.id, usuario.rol, canal) &&
    personaAcepta(usuario, canal);
}

/**
 * One grid cell, with the reason it is off.
 *
 * The `bajaPersonal` state is the whole point: the owner ticked the box and the
 * person still will not receive it. Without surfacing that, the screen lies.
 *
 * @param {Object} config
 * @param {Object} usuario
 * @param {string} canal
 * @return {{aplica: boolean, asignado: boolean, bajaPersonal: boolean, envia: boolean}}
 */
export function estadoCelda(config, usuario, canal) {
  const aplica = Boolean(usuario) && canalAplicaARol(canal, usuario.rol);
  if (!aplica) {
    return { aplica: false, asignado: false, bajaPersonal: false, envia: false };
  }
  const asignado = orgAsigna(config, usuario.id, usuario.rol, canal);
  const acepta = personaAcepta(usuario, canal);
  return {
    aplica: true,
    asignado,
    // Only worth flagging when the org DID assign it.
    bajaPersonal: asignado && !acepta,
    envia: asignado && acepta,
  };
}

/**
 * The write for toggling one cell. Returns a dotted-path update so two owners
 * editing different members cannot clobber each other's rows.
 *
 * @param {string} uid
 * @param {string} canal
 * @param {boolean} valor
 * @return {Object} updateDoc payload
 */
export function toggleCelda(uid, canal, valor) {
  return { [`miembros.${uid}.${canal}`]: Boolean(valor) };
}

// ── Scheduling ──────────────────────────────────────────────────────────────

// The cron runs every 15 minutes; an org fires on ONE of the four quarters,
// derived from its id. Same hour for the user, spread load for us — nobody
// picks a minute and every org does not stampede at :00.
export const CUARTOS = [0, 15, 30, 45];

/**
 * Which quarter-hour this org sends on. Deterministic: the same org always
 * lands on the same minute, so a retry on the next cron tick is a no-op rather
 * than a second email.
 *
 * @param {string} orgId
 * @return {number} 0 | 15 | 30 | 45
 */
export function cuartoDeOrg(orgId) {
  const s = String(orgId || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 100000;
  }
  return CUARTOS[h % CUARTOS.length];
}

/**
 * The org's local wall clock, for deciding whether it is its turn.
 *
 * Uses Intl rather than offset math because Tijuana still observes US DST
 * while the rest of the country does not — a fixed offset would drift by an
 * hour half the year.
 *
 * @param {number} nowMs
 * @param {string} zona  - IANA id
 * @return {{hora: number, minuto: number, dia: number, fecha: string}}
 *   `dia` is ISO weekday (1 = Monday); `fecha` is YYYY-MM-DD local.
 */
export function relojLocal(nowMs, zona) {
  const d = new Date(nowMs);
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: zona,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const DIAS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
  return {
    // Intl can render midnight as "24" in some locales; normalize.
    hora: Number(partes.hour) % 24,
    minuto: Number(partes.minute),
    dia: DIAS[partes.weekday] || 1,
    fecha: `${partes.year}-${partes.month}-${partes.day}`,
  };
}

/**
 * Is it this org's turn, for this canal, on this cron tick?
 *
 * @param {Object} config  - resolved config
 * @param {string} orgId
 * @param {string} canal
 * @param {number} nowMs
 * @return {boolean}
 */
export function esSuTurno(config, orgId, canal, nowMs) {
  const meta = CANAL_META[canal];
  if (!meta || !meta.programado) return false;
  const reloj = relojLocal(nowMs, config.zona);
  if (reloj.hora !== config.hora) return false;
  // The cron ticks at :00/:15/:30/:45; allow the whole quarter so a tick that
  // lands a few seconds late still matches.
  const cuarto = cuartoDeOrg(orgId);
  if (Math.floor(reloj.minuto / 15) * 15 !== cuarto) return false;
  if (meta.frecuencia === 'semanal' && reloj.dia !== config.diaSemanal) return false;
  // 'persona': the hour is the org's; the weekday is decided per member with
  // diaToca(frecuenciaDe(config, usuario), reloj.dia, config).
  return true;
}

/**
 * Both layers plus the person's cadence, for the scheduled digest.
 * @param {Object} config
 * @param {Object} usuario
 * @param {number} dia  - ISO weekday in the org zone
 * @return {boolean}
 */
export function seEnviaResumenHoy(config, usuario, dia) {
  if (!seEnvia(config, usuario, CANALES.RESUMEN_SEMANAL)) return false;
  return diaToca(frecuenciaDe(config, usuario), dia, config);
}

/**
 * Idempotency key for a send. Stamped on usuarios/{uid} so a retry, a second
 * instance, or a cron tick that overlaps cannot send twice.
 *
 * Keyed on the org's LOCAL date, not UTC — otherwise an org west of UTC would
 * get two sends on the day the UTC date rolls over mid-evening.
 *
 * @param {string} canal
 * @param {string} fechaLocal  - YYYY-MM-DD from relojLocal
 * @return {string}
 */
export function claveEnvio(canal, fechaLocal) {
  return `${canal}:${fechaLocal}`;
}
